/**
 * ParentAuthContext — Supabase auth surface for the parent dashboard
 * (#PL-WEB-PARENT).
 *
 * The parent session is a separate `supabase.auth` session from the
 * clinician's. The parent account flow never assumes an existing SLP
 * session. We share the underlying `supabase-js` client (so the user
 * really is signed in at the SDK level) but expose a parallel context
 * so:
 *   1. The parent screens only consume `useParentAuth()`, not the
 *      SLP-side `useAuth()`. This avoids accidental coupling.
 *   2. The magic-link redirectTo is `<origin>/parent/dashboard` for
 *      parents, vs. `<origin>/auth/callback` for SLPs.
 *
 * Both contexts read the same `supabase.auth` session and re-render
 * on the same onAuthStateChange event. The split is purely a
 * routing/redirect-target concern.
 *
 * The Supabase magic-link confirmation lands the parent on
 * `/parent/dashboard` (we set emailRedirectTo below). The dashboard
 * reads the URL fragment and calls setSession via the same
 * AuthCallbackScreen-style flow + redirects to `/parent/consent` if
 * no parent_accounts row exists yet.
 */
import { createContext, useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabase/client';

export interface ParentAuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithEmail: (input: { email: string }) => Promise<void>;
  signOut: () => Promise<void>;
}

const ParentAuthContext = createContext<ParentAuthContextValue | null>(null);
export { ParentAuthContext };

function getParentRedirectTo(): string {
  // The parent magic-link flow lands on /parent/dashboard. Cloudflare
  // Pages serves index.html for that path (see web/public/_redirects)
  // and the SPA's router then calls supabase.auth.setSession with the
  // URL-fragment tokens.
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/parent/dashboard`;
  }
  return '/parent/dashboard';
}

export interface ParentAuthProviderProps {
  children: ReactNode;
}

export function ParentAuthProvider({
  children,
}: ParentAuthProviderProps): ReactElement {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });
    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  async function signInWithEmail(input: { email: string }): Promise<void> {
    const { error } = await supabase.auth.signInWithOtp({
      email: input.email,
      options: { emailRedirectTo: getParentRedirectTo() },
    });
    if (error) throw new Error(error.message);
  }

  async function signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
  }

  const value: ParentAuthContextValue = {
    user: session?.user ?? null,
    session,
    loading,
    signInWithEmail,
    signOut,
  };

  return (
    <ParentAuthContext.Provider value={value}>
      {children}
    </ParentAuthContext.Provider>
  );
}
